import type { MouseEventHandler } from 'react';
import { useCallback } from 'react';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { openWindowByLink } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import type { WheelRewardType } from '../wheel';
import { Button } from './button';
import { ContentContainer, Decline, Disclaimer } from './content';
import { Coupon } from './coupon';
import { Title } from './title';

interface ContentProps {
  spin: () => void;
  closePopup: () => void;
  winningReward?: WheelRewardType;
  className?: string;
  isMobile?: boolean;
}

export const RewardContent = ({
  className,
  spin,
  closePopup,
  winningReward,
  isMobile
}: ContentProps) => {
  const { textColor, accentColor, editMode, googleAnalytics, widgetId } =
    useSettings();
  const { t } = useLingui();
  const declineText = t`No, Thank you`;

  const openLinkHandler: MouseEventHandler = useCallback(
    (e) => {
      if (winningReward?.type === 'spinAgain') {
        spin();
        return;
      }

      // Google Analytics: Prize Button Clicked event
      sendGoogleAnalyticsEvents({
        eventType: 'prizeButtonClicked',
        googleAnalyticsEvents: googleAnalytics,
        valueMapping: {
          widgetId,
          prizeName: winningReward?.label,
          prizeId: winningReward?.id,
          buttonLabel: winningReward?.actionButtonText,
          buttonUrl: winningReward?.actionButtonLink?.value
        }
      });

      if (winningReward?.actionButtonLink?.value) {
        openWindowByLink(e, {
          ...winningReward?.actionButtonLink,
          ...(editMode ? { target: '_blank' } : {})
        });
      }
    },
    [winningReward, closePopup, googleAnalytics, widgetId]
  );

  // Google Analytics: Coupon Copied event
  const handleCouponCopied = useCallback(
    (couponValue: string) => {
      sendGoogleAnalyticsEvents({
        eventType: 'couponCopied',
        googleAnalyticsEvents: googleAnalytics,
        valueMapping: {
          widgetId,
          prizeName: winningReward?.label,
          prizeId: winningReward?.id,
          coupon: couponValue
        }
      });
    },
    [googleAnalytics, widgetId, winningReward]
  );

  return (
    <ContentContainer className={className}>
      <Title
        title={winningReward?.heading}
        caption={winningReward?.text}
        isMobile={isMobile}
        textColor={textColor}
      />

      {!!winningReward?.promocode && (
        <Coupon
          textColor={textColor}
          coupon={winningReward?.promocode}
          messages={{
            copied: t`Copied!`,
            clickToCopy: t`Click To Copy`
          }}
          onCopied={handleCouponCopied}
        />
      )}

      {winningReward?.actionButtonText && (
        <Button
          onClick={openLinkHandler}
          buttonText={winningReward?.actionButtonText}
          isMobile={isMobile}
          accentColor={accentColor}
        />
      )}

      {!!declineText && (
        <Decline
          _fontSize={isMobile ? 16 : 18}
          onClick={closePopup}
          _color={textColor}
        >
          {declineText}
        </Decline>
      )}
      {!!winningReward?.footerText && (
        <Disclaimer
          dangerouslySetInnerHTML={{ __html: winningReward?.footerText }}
          _color={textColor}
        />
      )}
    </ContentContainer>
  );
};
